import { useState } from "react";
import { LocaleSelectorModal } from "@/components/layout/LocaleSelectorModal";
import { useLocaleOptionsContext } from "@/contexts/LocaleOptionsContext";
import type { CountryWithCurrency } from "@/api/locale";
import { getLocale } from "@/utils/locale-store";
import { fillTemplate } from "@/utils/text";
import { t, useLocalizedTexts } from "@/utils/translations";
import styles from "./CurrencySwitchDialog.module.css";

interface ChangeMarketActionProps {
  /**
   * Lowercase ISO currency the member CAN be billed in — their pinned one. The country
   * selector opens pre-set to a market that sells in it, so applying is one click.
   */
  billingCurrency: string | null;
  /** Rendered as the primary action rather than the secondary one. */
  isPrimary?: boolean;
}

/**
 * The way out for a member who can neither switch currency nor pay in the cart's:
 * move to a market that sells in the currency they are billed in.
 *
 * Reuses the site's own country selector rather than navigating somewhere itself —
 * that modal already knows how to resolve the translated URL for a culture, handles
 * the separate Norwegian deployment, and persists the locale. A second, private
 * implementation of "change market" would drift from it immediately.
 *
 * Renders nothing when the locale options have not loaded, since without them there
 * is no country list to choose from.
 */
export function ChangeMarketAction({
  billingCurrency,
  isPrimary = false,
}: ChangeMarketActionProps) {
  const texts = useLocalizedTexts();
  const localeOptions = useLocaleOptionsContext();
  const [isSelectorOpen, setSelectorOpen] = useState(false);

  if (!localeOptions) {
    return null;
  }

  const current = getLocale();
  const match = findCountrySelling(localeOptions.countries, billingCurrency);

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${isPrimary ? styles.primary : ""}`.trim()}
        onClick={() => setSelectorOpen(true)}
      >
        {billingCurrency
          ? fillTemplate(
              t(
                texts,
                "Tracker.Checkout.CurrencySwitch.ChangeMarket",
                "Shop in {currency} instead"
              ),
              { currency: billingCurrency.toUpperCase() }
            )
          : t(
              texts,
              "Tracker.Checkout.CurrencySwitch.ChangeCountry",
              "Change country"
            )}
      </button>

      <LocaleSelectorModal
        isOpen={isSelectorOpen}
        onClose={() => setSelectorOpen(false)}
        localeOptions={localeOptions}
        // Pre-set to a market that sells in their currency, so the member only has to
        // confirm. Falls back to where they are when no market matches, which leaves
        // them a working selector rather than a broken one.
        currentCountry={match?.countryCode ?? current.country}
        currentLanguage={
          match?.validCultures[0]?.cultureCode ?? current.language
        }
      />
    </>
  );
}

/**
 * A country the shop sells to in this currency. Null when none does — possible when
 * the member's currency belongs to a market this deployment does not serve.
 */
function findCountrySelling(
  countries: CountryWithCurrency[],
  currency: string | null
): CountryWithCurrency | null {
  if (!currency) return null;
  return (
    countries.find(
      (country) =>
        country.currencyCode?.toLowerCase() === currency.toLowerCase() &&
        country.validCultures.length > 0
    ) ?? null
  );
}
